import React, { useState, useEffect } from 'react';
import api from '../api';
import { ModernCard, ModernButton, ModernBadge, ModernInput, ModernSelect } from './ui/modern';

const getRoleBadgeVariant = (role) => {
  switch (role) {
    case 'Admin': return 'danger';
    case 'Manager': return 'warning';
    case 'Technician': return 'info';
    case 'Client': return 'success';
    default: return 'default';
  }
};

const getRoleIcon = (role) => {
  switch (role) {
    case 'Admin': return '👑';
    case 'Manager': return '📊';
    case 'Technician': return '🔧';
    case 'Client': return '👤';
    default: return '👤';
  }
};

const UserList = ({ user, refreshSignal = 0 }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/users');
        setUsers(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching users:', error);
        setLoading(false);
      }
    };

    if (user.role === 'Admin') {
      fetchUsers();
    }
  }, [user, refreshSignal]);

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setEditFormData({ ...user, password: '' });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({ ...editFormData, [name]: value });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editFormData };
      // username is server-side derived from email; do not send to avoid validation issues
      delete payload.username;
      // if password left blank, remove from payload to avoid validation error
      if (!payload.password) delete payload.password;
      // if not client role, remove company fields
      if (payload.role !== 'Client') {
        delete payload.companyId;
        delete payload.companyTag;
      }
      const response = await api.put(`/users/${editingUser}`, payload);
      const updated = response.data || payload;
      setUsers(users.map(u => u.id === editingUser ? { ...u, ...updated, password: undefined } : u));
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      const msg = error?.response?.data?.error || 'Update failed';
      alert(msg);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${id}`);
        setUsers(users.filter(u => u.id !== id));
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  if (user.role !== 'Admin') {
    return null;
  }

  if (loading) {
    return (
      <ModernCard>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-white text-lg">Loading users...</div>
        </div>
      </ModernCard>
    );
  }

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'All' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <>
      {/* Filters */}
      <ModernCard className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-2">Search</label>
            <ModernInput
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-2">Filter by Role</label>
            <ModernSelect
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              options={[
                { value: 'All', label: 'All Roles' },
                { value: 'Admin', label: 'Admin' },
                { value: 'Manager', label: 'Manager' },
                { value: 'Technician', label: 'Technician' },
                { value: 'Client', label: 'Client' }
              ]}
            />
          </div>
        </div>
      </ModernCard>

      {/* User Stats */}
      <ModernCard className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-white mb-1">{users.length}</div>
            <div className="text-sm text-slate-400">Total Users</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-400 mb-1">
              {users.filter(u => u.role === 'Admin').length}
            </div>
            <div className="text-sm text-slate-400">Admins</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-amber-400 mb-1">
              {users.filter(u => u.role === 'Manager').length}
            </div>
            <div className="text-sm text-slate-400">Managers</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-400 mb-1">
              {users.filter(u => u.role === 'Technician').length}
            </div>
            <div className="text-sm text-slate-400">Technicians</div>
          </div>
        </div>
      </ModernCard>

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <ModernCard>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👥</div>
            <div className="text-xl text-white mb-2">No users found</div>
            <div className="text-slate-400">Try adjusting your search or filters</div>
          </div>
        </ModernCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map((u) => (
            <ModernCard key={u.id} className="hover:scale-105 transition-transform duration-300">
              {/* User Avatar & Name */}
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white truncate mb-1">{u.name}</h3>
                  <p className="text-sm text-slate-400 truncate">{u.email}</p>
                </div>
              </div>

              {/* Role Badge */}
              <div className="mb-4">
                <ModernBadge variant={getRoleBadgeVariant(u.role)} size="lg" className="w-full justify-center">
                  <span className="mr-2">{getRoleIcon(u.role)}</span>
                  {u.role}
                </ModernBadge>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <ModernButton 
                  variant="glass" 
                  onClick={() => handleEdit(u)}
                  className="flex-1"
                >
                  ✏️ Edit
                </ModernButton>
                <ModernButton 
                  variant="glass" 
                  onClick={() => handleDelete(u.id)}
                  className="flex-1 bg-red-500/10 hover:bg-red-500/20 text-red-400"
                >
                  🗑️ Delete
                </ModernButton>
              </div>
            </ModernCard>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <ModernCard className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Edit User</h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <ModernInput
                label="Name"
                type="text"
                name="name"
                value={editFormData.name}
                onChange={handleEditChange}
                required
              />
              <ModernInput
                label="Email"
                type="email"
                name="email"
                value={editFormData.email}
                onChange={handleEditChange}
                required
              />
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Role</label>
                <ModernSelect
                  name="role"
                  value={editFormData.role}
                  onChange={handleEditChange}
                  options={[
                    { value: 'Admin', label: '👑 Admin' },
                    { value: 'Manager', label: '📊 Manager' },
                    { value: 'Technician', label: '🔧 Technician' },
                    { value: 'Client', label: '👤 Client' }
                  ]}
                />
              </div>
              {editFormData.role === 'Client' && (
                <>
                  <ModernInput
                    label="Company ID (optional)"
                    type="text"
                    name="companyId"
                    value={editFormData.companyId || ''}
                    onChange={handleEditChange}
                  />
                  <ModernInput
                    label="Company Tag (optional)"
                    type="text"
                    name="companyTag"
                    value={editFormData.companyTag || ''}
                    onChange={handleEditChange}
                  />
                </>
              )}
              <ModernInput
                label="New Password (leave blank to keep current)"
                type="password"
                name="password"
                value={editFormData.password}
                onChange={handleEditChange}
                placeholder="••••••••"
              />
              <div className="flex gap-3 pt-4">
                <ModernButton type="submit" variant="gradient" className="flex-1">
                  💾 Save Changes
                </ModernButton>
                <ModernButton 
                  type="button" 
                  variant="glass" 
                  onClick={() => setEditingUser(null)}
                  className="flex-1"
                >
                  ✕ Cancel
                </ModernButton>
              </div>
            </form>
          </ModernCard>
        </div>
      )}
    </>
  );
};

export default UserList; 